<!-- left: image/upload area | right: description panel (hidden in compactLayout) -->
<div class="flex gap-7">
  <!-- image container -->
  <div
    [style.aspect-ratio]="containerAspectRatio ? containerAspectRatio : aspectRatio"
    [style.width]="width ? width : '100%'"
    [style.height]="height"
    class="flex justify-center items-center h-full temp-main-container relative"
  >
    <input
      type="file"
      hidden
      #fileInput
      (change)="onFileChange($event)"
      [accept]="accepts"
    />
    <!-- empty and uploading states -->
    @if (!imageUrl) {
      <div
        class="h-full w-full"
        [style.aspect-ratio]="containerAspectRatio ? containerAspectRatio : aspectRatio"
      >
        <!-- empty -->
        @if (uploadProgress === 0 && !errorMessage) {
          <div
            class="file-input-container h-full w-full flex flex-col justify-center items-center rounded text-center p-3 text-base font-normal transition-all duration-300 ease-in-out cursor-pointer"
            [class.dragging-over]="draggingOver"
            role="button"
            tabindex="0"
            (click)="openFileInput($event)"
            (keydown.enter)="openFileInput($event)"
            (keydown.space)="openFileInput($event)"
          >
            <label class="flex flex-col justify-center items-center cursor-pointer">
              @if (mockImageUrl) {
                <img [src]="mockImageUrl" class="mb-1" />
              } @else {
                <fa-icon [icon]="'file-image'" [style.font-size]="iconSize + 'px'"></fa-icon>
              }
              @if (!draggingOver) {
                <div class="min-w-16">
                  <!-- prettier-ignore -->
                  <span class="whitespace-pre-wrap mr-1">{{ 'image-input.main-text' | translate }}</span>
                  <!-- prettier-ignore -->
                  <span class="load-file-link cursor-pointer font-bold underline whitespace-pre-wrap">{{ 'image-input.load' | translate }}</span>
                </div>
              } @else {
                <span class="font-bold">{{
                  'image-input.dragging-over-text' | translate
                }}</span>
              }
            </label>
          </div>
        }
        <!-- error state -->
        @if (uploadProgress === 0 && errorMessage) {
          <div
            class="file-input-container input-error h-full w-full flex flex-col justify-center items-center rounded text-center p-3 text-base font-normal transition-all duration-300 ease-in-out relative"
            [class.dragging-over]="draggingOver"
          >
            <label class="flex flex-col justify-center items-center gap-2">
              @if (!draggingOver) {
                <fa-icon
                  class="text-2xl"
                  [icon]="'exclamation-triangle'"
                ></fa-icon>
                <div class="min-w-16">
                  <!-- prettier-ignore -->
                  <div class="whitespace-pre-wrap text-error">{{ errorMessage | translate }}</div>
                  <!-- prettier-ignore -->
                  <span class="load-file-link cursor-pointer font-bold underline whitespace-pre-wrap absolute bottom-3 left-1/2 -translate-x-1/2 text-center" (click)="openFileInput($event)">{{ 'image-input.load' | translate }}</span>
                </div>
              } @else {
                <span class="font-bold">{{
                  'image-input.dragging-over-text' | translate
                }}</span>
              }
            </label>
          </div>
        }
        <!-- uploading -->
        @if (uploadProgress > 0 && uploadProgress < 100) {
          <div class="upload-progress-container w-full h-full rounded">
            <div class="relative w-full h-full rounded-full flex justify-center items-center flex-col">
              <circle-progress
                [percent]="uploadProgress"
                [radius]="radius"
                [showTitle]="false"
                [outerStrokeWidth]="strokeWidth"
                [outerStrokeColor]="'#fff'"
                [showInnerStroke]="false"
                [showSubtitle]="false"
                [showUnits]="false"
                [showBackground]="false"
                [startFromZero]="false"
              ></circle-progress>
              <div class="absolute text-2xl text-white font-bold">{{ uploadProgress }}%</div>
              @if (!hideCancelButton) {
                <button class="absolute bottom-4 border-none bg-transparent text-white text-base font-normal cursor-pointer underline" (click)="cancelUpload()">
                  {{ 'image-input.cancel' | translate }}
                </button>
              }
            </div>
          </div>
        }
      </div>
    } @else {
      <!-- existing image state -->
      <div
        class="image-container rounded h-full relative flex justify-center items-center"
        [class.compact-layout]="compactLayout"
        [style.aspect-ratio]="aspectRatio"
        [class.horizontal]="horizontal"
      >
        <img class="object-cover rounded w-full h-full" [src]="imageUrl" [attr.alt]="'image-upload.alt' | translate" />
        @if (compactLayout) {
          <rk-button
            class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            [variant]="'secondary'"
            [text]="'create-app-form.change-image' | translate"
            (buttonClick)="openFileInput($event)"
          ></rk-button>
        }
        @if (deleteButton) {
          <span
            class="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 bottom-4 text-white underline text-center cursor-pointer"
            (click)="removeImage()"
            >{{ 'image-upload.delete' | translate }}</span
          >
        }
      </div>
    }
  </div>
  <!-- right section container -->
  <!-- description and technical requirements -->
  @if (!compactLayout) {
    <div
      class="flex flex-col justify-between flex-1"
      [class.pt-3]="title === null"
    >
      <div>
        @if (title) {
          <div class="font-bold mb-2">{{ title | translate }}</div>
        }
        @if (description) {
          <div class="mb-4">{{ description | translate }}</div>
        }
        <!-- technical requirements -->
        <label class="font-bold" [class.text-sm]="title !== null">{{
          'image-upload.constraints-title' | translate
        }}</label>
        <!-- prettier-ignore -->
        <p class="opacity-70 text-base whitespace-pre-wrap" [class.text-sm]="title !== null">{{ technicalRequirementsText }}</p>
      </div>
      <!-- change and delete button -->
      <div class="edit-buttons mt-2" [class.visible]="uploadProgress >= 100 || imageUrl">
        <rk-button
          [variant]="'secondary'"
          [text]="'image-upload.change-image' | translate"
          (buttonClick)="openFileInput($event)"
        ></rk-button>
        @if (deleteButton) {
          <rk-button
            class="ml-4"
            [variant]="'danger'"
            [icon]="'trash'"
            [iconOnly]="true"
            (buttonClick)="removeImage()"
          ></rk-button>
        }
      </div>
    </div>
  }
</div>
